import { Box } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { archiveConfirmationWord, removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { ConfirmationDialogResult, RecordUserRemoveType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IRecordEdit } from "../../../models/record/recordEdit";
import { IRecordEditItem } from "../../../models/record/recordEditItem";
import * as recordService from "../../../models/services/recordService";
import { ISessionState } from "../../../models/session/sessionState";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { RecordEdit as RecordEditView, RecordEditViewMode } from "../RecordEdit";
import { RecordEditRemoveSelectTypeDialog } from "./RecordEditRemoveSelectTypeDialog";

export interface IRecordEditDialogProp {
    sessionState: ISessionState;
    companyId: string;
    recordId: string;
    classes?: string;
    hasPrevious: boolean;
    hasNext: boolean;
    defaultMenuItem: RecordEditViewMode;
    onOk: () => void;
    onCancel: (listRefreshNeeded: boolean) => void;
    onRefreshList: () => Promise<void>;
    onNewSaved: (recordId: string) => void;
    onPrevious: () => void;
    onNext: () => void;
    onRefreshSessionState: () => void;
    onEmploymentSaved: () => void;
}

export interface IRecordEditDialogState {
    recordEditViewMode: RecordEditViewMode;
    recordEdit: IRecordEdit;
    record: IRecordEditItem;
    showRemoveTypeDialog: boolean;
    hasSheetData: boolean;
}

export class RecordEditDialog extends React.Component<IRecordEditDialogProp, IRecordEditDialogState> {
    private recordEditViewRef = React.createRef<RecordEditView>();

    constructor(props: IRecordEditDialogProp) {
        super(props);
        this.state = {
            recordEditViewMode: Base.isNullOrUndefined(props.defaultMenuItem) ? RecordEditViewMode.General : props.defaultMenuItem,
            recordEdit: null,
            record: null,
            showRemoveTypeDialog: false,
            hasSheetData: false
        };
    }

    loadRecord = async (id: string) => {
        const props = this.props;
        const result = await AppUtils.callService(() => recordService.getRecordEdit(id, props.companyId), false);
        if (!result) {
            //Close dialog if record not found and nothing is loaded
            if (!this.state.recordEdit) {
                props.onCancel(false);
            }
            return;
        }
        this.setState({
            recordEdit: result,
            record: result.record,
            hasSheetData: !!result.taskType
        });
    };

    componentDidMount(): void {
        this.loadRecord(this.props.recordId);
    }

    async componentDidUpdate(prevProps: IRecordEditDialogProp, prevState: IRecordEditDialogState): Promise<void> {
        const props = this.props;
        const prevDefaultMenuItem = Base.isNullOrUndefined(prevProps.defaultMenuItem) ? RecordEditViewMode.General : prevProps.defaultMenuItem;
        const defaultMenuItem = Base.isNullOrUndefined(props.defaultMenuItem) ? RecordEditViewMode.General : props.defaultMenuItem;
        if (prevDefaultMenuItem === defaultMenuItem) return;
        this.setState({
            recordEditViewMode: defaultMenuItem
        });
    }

    handleToggleActiveMenuItem = (recordEditViewMode: RecordEditViewMode) => {
        this.setState({ recordEditViewMode });
    };

    handleRecordLoaded = (recordEdit: IRecordEdit) => {
        this.setState({
            recordEdit,
            record: recordEdit.record,
            hasSheetData: !!recordEdit.taskType,
        });
    };

    getRecordDataWasModified = (): boolean => {
        return this.recordEditViewRef && this.recordEditViewRef.current && this.recordEditViewRef.current.getRecordDataWasModified();
    };
        
    getAttahmentsCount = (): number => {
        if (this.recordEditViewRef && this.recordEditViewRef.current) {
            return this.recordEditViewRef.current.getAttahmentsCount();
        } else {
            return 0;
        }
    };

    handlePrint = async () => {
        const state = this.state;
        if (!state.record) return;
        const fileName = await AppUtils.callService(() => recordService.getRecordPrint(state.record.id, this.props.companyId));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    handleSave = async (closeDialog: boolean, activeState: number = null) => {
        const props = this.props;
        const state = this.state;
        const isNew = state.record.isNew();
        if (!this.recordEditViewRef || !this.recordEditViewRef.current) return;
        const savedRecordId = await this.recordEditViewRef.current.saveToDb(activeState);
        if (!savedRecordId) return;
        if (!isNew) {
            if (!closeDialog) {
                this.recordEditViewRef.current.loadRecord();
                props.onOk();
                return;
            }
            props.onCancel(true);
        } else {
            props.onNewSaved(savedRecordId);
        }
    };

    remove = async (removeType: RecordUserRemoveType) => {
        const props = this.props;
        const state = this.state;
        if (!state.record || state.record.isNew()) return;
        // Call server
        const result = await AppUtils.callService(() => recordService.removeRecord(state.record.id, props.companyId, removeType));
        if (!result) return;
        this.setState({
            showRemoveTypeDialog: false
        });
        props.onCancel(true);
    };

    handleUserEditRemoveSelectTypeDialogOk = async (removeType: RecordUserRemoveType) => {
        await this.remove(removeType);
    };

    handleUserEditRemoveSelectTypeDialogCancel = () => {
        this.setState({
            showRemoveTypeDialog: false
        });
    };

    handleRemove = async () => {
        const state = this.state;
        if (state.recordEdit.isPartnerCompanyRecord || !state.record || state.record.isNew()) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveRecordUserParameter, state.record.getName()), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            if (state.record.userCode) {
                this.setState({
                    showRemoveTypeDialog: true
                });
            } else {
                await this.remove(RecordUserRemoveType.RemoveRecordUser);
            }
        }
    };

    // #region Archive
    handleArchive = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToArchiveRecordUserParameter, this.state.record.getName()), false, archiveConfirmationWord) !== ConfirmationDialogResult.Yes) {
            return;
        }
        await this.handleSave(true, 0);
    };

    handleRestoreFromArchive = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRestoreRecordUserParameterFromArchive, this.state.record.getName()), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        await this.handleSave(true, 1);
    };
    // #endregion Archive

    handleCancel = async () => {
        const props = this.props;
        if (this.recordEditViewRef && this.recordEditViewRef.current) {
            const cancelResult = await await this.recordEditViewRef.current.cancel(false);
            if (cancelResult === ConfirmationDialogResult.Cancel) return;
            if (cancelResult === ConfirmationDialogResult.Yes) {
                this.handleSave(true);
            } else {
                props.onCancel(this.getRecordDataWasModified());
            }
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        if (!this.recordEditViewRef || !this.recordEditViewRef.current) return false;
        const cancelResult = await this.recordEditViewRef.current.cancel(true);
        if (cancelResult === ConfirmationDialogResult.Cancel) return false;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            await props.onRefreshList();
        }
        return true;
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.recordEdit) return null;
        const record = state.record;
        const isNew = !record || record.isNew();

        const isArchived = record?.activeState === 0;
        const canArchive = !isNew && state.recordEdit.canArchiveAndRemove && !isArchived;
        const canRestoreFromArchive = !isNew && state.recordEdit.canArchiveAndRemove && isArchived;

        const attachmentsCount = this.getAttahmentsCount();
        return (
            <Dialog
                id="record-edit-dialog"
                maxWidth="xl"
                maxHeight={"951px"}
                title={!record ? Translations.Loading + "..." : (isNew ? Translations.AddRecordUser : (Translations.EditRecordUser + ": " + record.getName()))}
                dialogTabs={
                    <DialogTabs
                        activeTabId={state.recordEditViewMode}
                        tabs={[
                            { id: RecordEditViewMode.General, title: Translations.BasicInformation },
                            { id: RecordEditViewMode.Familiarization, title: Translations.Familiarization },
                            { id: RecordEditViewMode.Qualifications, title: Translations.Qualifications },
                            { id: RecordEditViewMode.Education, title: Translations.Education + " / " + Translations.Competency },
                            { id: RecordEditViewMode.Employment, title: Translations.Employment },
                            { id: RecordEditViewMode.NextOfKin, title: Translations.NextOfKin },
                            { id: RecordEditViewMode.Salary, title: Translations.Salary, hidden: !state.hasSheetData },
                            { id: RecordEditViewMode.Attachments, title: Translations.Attachments, badgeContent: attachmentsCount },
                            { id: RecordEditViewMode.TransferredProperty, title: Translations.TransferredProperties },
                            { id: RecordEditViewMode.Log, title: Translations.Log },
                        ]}
                        onChangeTab={this.handleToggleActiveMenuItem}
                    />
                }
                body={
                    <>
                        <RecordEditView
                            ref={this.recordEditViewRef}
                            onDialog={true}
                            sessionState={props.sessionState}
                            recordEdit={state.recordEdit}
                            companyId={props.companyId}
                            recordId={props.recordId}
                            viewMode={state.recordEditViewMode}
                            onRefreshSessionState={props.onRefreshSessionState}
                            onRecordLoaded={this.handleRecordLoaded}
                            onEmploymentSaved={props.onEmploymentSaved}
                        />
                        {state.showRemoveTypeDialog &&
                            <RecordEditRemoveSelectTypeDialog
                                onOk={this.handleUserEditRemoveSelectTypeDialogOk}
                                onCancel={this.handleUserEditRemoveSelectTypeDialogCancel}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={this.handlePrevious}
                            onNext={this.handleNext}
                            modifyUser={!isNew ? state.record.modifyUser : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onPrint={this.handlePrint}
                            onRemove={!state.recordEdit.isPartnerCompanyRecord && !isNew
                                ? this.handleRemove
                                : null}
                            onArchive={canArchive
                                ? this.handleArchive
                                : null}
                            onRestoreFromArchive={canRestoreFromArchive
                                ? this.handleRestoreFromArchive
                                : null
                            }
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}